/**
 * Energikalkylator: målkh från tid × g/h och förenklad fördelning till sortiment (dryck/gel/mat).
 * Ingen modell för faktisk energiförbrukning — användarens antaganden styr allt.
 */

/** Från Woo: caffeine_mg, contains_caffeine, koffein m.m. */
export type CaffeineKind = "contains" | "none" | "unknown";

export type CalcProduct = {
  id: number;
  name: string;
  slug: string;
  price: string;
  image: string | null;
  inStock: boolean;
  shippingClass: string;
  carbs: number | null;
  servings_per_package: number | null;
  carbs_per_package: number | null;
  package_price_adjustment?: number;
  caffeine?: CaffeineKind;
  /** mg per portion om satt i Woo (caffeine_mg_per_serving / caffeine_mg) */
  caffeine_mg_per_serving?: number | null;
};

/** Kh-fördelning gäller endast dryck / gel / mat (inte flaskor). */
export type NutritionCategoryKey = "dryck" | "gel" | "mat";

/** Samma nycklar som i server/packages.js (sport på paket). */
export const ENERGY_CALC_SPORTS = ["triathlon", "running", "cycling", "skiing"] as const;
export type EnergySport = (typeof ENERGY_CALC_SPORTS)[number];

export const ENERGY_SPORT_LABELS: Record<EnergySport, string> = {
  triathlon: "Triathlon",
  running: "Löpning",
  cycling: "Cykel",
  skiing: "Längdskidor",
};

export function isEnergySport(value: string | null | undefined): value is EnergySport {
  return (
    value != null && (ENERGY_CALC_SPORTS as readonly string[]).includes(value)
  );
}

export type TriathlonSegment = "swim" | "bike" | "run";

/** Vilka av dryck/gel/mat som får väljas under loppet (server: energy-calculator.config.js) */
export type EnergySlotConfig = {
  sportNutritionSlots: Record<EnergySport, NutritionCategoryKey[]>;
  triathlonSegmentSlots: Record<TriathlonSegment, NutritionCategoryKey[]>;
};

export type EnergyCatalog = {
  dryck: CalcProduct[];
  gel: CalcProduct[];
  mat: CalcProduct[];
  /** Vätskeflaskor / softflaskor — samma lista som API (produkt-ID:n). */
  flaskor: CalcProduct[];
  /** Pool från server (productIds.prerace) */
  prerace: CalcProduct[];
  energySlotConfig?: EnergySlotConfig;
  /**
   * Smak-/variantgrupper: varje lista är utbytbara Woo-produkt-ID (samma roll, olika smak).
   * Byggs från produktmeta energy_flavor_group (fallback: energy-calculator.config.js).
   */
  flavorGroups?: number[][];
  /** Föredragen flaska (meta energy_default_bottle eller config). */
  defaultBottleProductId?: number | null;
};

export const CAFFEINE_FILTER_MODES = ["contains", "none"] as const;
export type CaffeineFilterMode = (typeof CAFFEINE_FILTER_MODES)[number];

export const CAFFEINE_FILTER_LABELS: Record<CaffeineFilterMode, string> = {
  contains: "Med koffein",
  none: "Utan koffein",
};

export function isCaffeineFilterMode(
  value: string | null | undefined
): value is CaffeineFilterMode {
  return (
    value != null &&
    (CAFFEINE_FILTER_MODES as readonly string[]).includes(value)
  );
}

export function productCaffeineKind(p: CalcProduct): CaffeineKind {
  return p.caffeine ?? "unknown";
}

export function caffeineBadgeSv(p: CalcProduct): string | null {
  const k = productCaffeineKind(p);
  const mg = p.caffeine_mg_per_serving;
  if (k === "contains") {
    if (mg != null && Number.isFinite(mg) && mg > 0) {
      return `Koffein ~${mg} mg/serving`;
    }
    return "Koffein";
  }
  if (k === "none") return "Koffeinfri";
  return null;
}

/**
 * Under loppet: "Med koffein" = inget koffeinfiltre på sortimentet (vanlig sportdryck/gel).
 * "Utan koffein" = undvik produkter uttryckligen markerade med koffein; okända meta (`unknown`) visas
 * så katalogen inte blir tom när Woo inte är fullständigt märkt.
 */
export function matchesCaffeineFilter(
  p: CalcProduct,
  mode: CaffeineFilterMode
): boolean {
  const k = productCaffeineKind(p);
  if (mode === "contains") return true;
  return k !== "contains";
}

export function filterCatalogByCaffeine(
  catalog: EnergyCatalog,
  mode: CaffeineFilterMode
): EnergyCatalog {
  if (mode === "contains") {
    return catalog;
  }
  return {
    ...catalog,
    dryck: catalog.dryck.filter((p) => matchesCaffeineFilter(p, mode)),
    gel: catalog.gel.filter((p) => matchesCaffeineFilter(p, mode)),
    mat: catalog.mat.filter((p) => matchesCaffeineFilter(p, mode)),
    flaskor: catalog.flaskor,
    prerace: (catalog.prerace ?? []).filter((p) =>
      matchesCaffeineFilter(p, mode)
    ),
  };
}

export function catalogHasAnyUsableProduct(catalog: EnergyCatalog): boolean {
  const keys: NutritionCategoryKey[] = ["dryck", "gel", "mat"];
  return keys.some((k) => catalog[k].some(isUsableForCalculator));
}

/** Kolhydrater per köpt enhet (påse/burk eller enstaka portion om inget annat angivits). */
export function effectiveCarbsPerUnit(p: CalcProduct): number {
  if (p.carbs_per_package != null && p.carbs_per_package > 0) {
    return p.carbs_per_package;
  }
  const c = p.carbs ?? 0;
  const s = p.servings_per_package;
  if (s != null && s > 0 && c > 0) {
    return s * c;
  }
  if (c > 0) return c;
  return 0;
}

export function isUsableForCalculator(p: CalcProduct): boolean {
  return effectiveCarbsPerUnit(p) > 0;
}

/** Hitta produkt i katalogen (alla pooler inkl. flaskor och prerace). */
export function findProductInEnergyCatalog(
  catalog: EnergyCatalog,
  id: number
): CalcProduct | null {
  const pools: CalcProduct[][] = [
    catalog.prerace ?? [],
    catalog.dryck,
    catalog.gel,
    catalog.mat,
    catalog.flaskor ?? [],
  ];
  for (const pool of pools) {
    const p = pool.find((x) => x.id === id);
    if (p) return p;
  }
  return null;
}

/** Stabil nyckel för smakval per rad i tabellen. */
export function packageLineFlavorKey(line: PackageLine): string {
  return line.lineId ?? `${line.categoryKey}-${line.product.id}`;
}

/** Samma smak-/inkludering för rader som splittats från en rad (`…-split0`, `…-split1`). */
export function packageLineFlavorGroupKey(line: PackageLine): string {
  return packageLineFlavorKey(line).replace(/-split\d+$/, "");
}

/** Produkt-ID i samma smakgrupp som radens produkt (tom om ingen grupp). */
export function flavorPeerProductIdsForLine(
  catalog: EnergyCatalog,
  line: PackageLine
): number[] {
  const groups = catalog.flavorGroups;
  if (!groups || groups.length === 0) return [];
  const id = line.product.id;
  for (const g of groups) {
    if (g.includes(id)) return [...g];
  }
  return [];
}

/**
 * Alla produkter i samma kategoripool som kan väljas i stället för radens produkt (gel ↔ annan gel, bar ↔ annan bar …).
 * Klarar koffeinfiltret. Poster i `flavorGroups` (samma smaklinje) sorteras överst; därefter i lager och namn.
 * Före start (prerace) är låsta — inga alternativ.
 */
export function flavorAlternativesForLine(
  catalog: EnergyCatalog,
  line: PackageLine,
  caffeineMode: CaffeineFilterMode
): CalcProduct[] {
  const catKey = line.categoryKey;
  if (catKey === "prerace" || line.preraceIntakeHint != null) {
    return [];
  }
  if (
    catKey !== "dryck" &&
    catKey !== "gel" &&
    catKey !== "mat" &&
    catKey !== "flaskor"
  ) {
    return [];
  }
  const pool = catalog[catKey];
  const peerSet = new Set(flavorPeerProductIdsForLine(catalog, line));
  const out = pool.filter((p) => {
    if (!matchesCaffeineFilter(p, caffeineMode)) return false;
    if (catKey === "flaskor") return true;
    return isUsableForCalculator(p);
  });
  out.sort((a, b) => {
    const pa = peerSet.has(a.id) ? 0 : 1;
    const pb = peerSet.has(b.id) ? 0 : 1;
    if (pa !== pb) return pa - pb;
    const stock = Number(b.inStock) - Number(a.inStock);
    if (stock !== 0) return stock;
    return String(a.name || "").localeCompare(String(b.name || ""), "sv", {
      sensitivity: "base",
    });
  });
  return out;
}

/**
 * Byt produkt på en rad. För icke-tillbehör med kh-mål: antal sätts till minsta heltal så att
 * levererade kh ≥ radens `allocationTarget` (kan bli fler enheter om den nya produkten har lägre kh/st).
 * Före start (prerace) byts aldrig.
 */
export function resolvePackageLineWithFlavor(
  line: PackageLine,
  chosenProductId: number | undefined,
  catalog: EnergyCatalog
): PackageLine {
  if (line.categoryKey === "prerace" || line.preraceIntakeHint != null) {
    return line;
  }
  if (chosenProductId == null || chosenProductId === line.product.id) {
    return line;
  }
  const product = findProductInEnergyCatalog(catalog, chosenProductId);
  if (!product) return line;
  if (line.accessory) {
    return { ...line, product };
  }
  const perUnit = effectiveCarbsPerUnit(product);
  if (perUnit <= 0) {
    return { ...line, product };
  }
  if (line.allocationTarget <= 0) {
    return { ...line, product };
  }
  const units = Math.max(1, Math.ceil(line.allocationTarget / perUnit));
  const carbsDelivered = Math.round(units * perUnit);
  return { ...line, product, units, carbsDelivered };
}

/** Tillåten avvikelse (g) mellan levererat och radens fördelade mål efter produktbyte. */
export const CARB_ALLOC_LINE_TOLERANCE_G = 1;

/**
 * Jämför levererade kh mot radens `allocationTarget` (t.ex. efter gelbyte med omräknat antal).
 */
export function carbAllocationMismatchForLine(line: PackageLine): {
  ok: boolean;
  delta: number;
} {
  if (
    line.accessory ||
    line.preraceIntakeHint != null ||
    line.allocationTarget <= 0
  ) {
    return { ok: true, delta: 0 };
  }
  const delta = line.carbsDelivered - line.allocationTarget;
  const ok = Math.abs(delta) <= CARB_ALLOC_LINE_TOLERANCE_G;
  return { ok, delta };
}

/** Sant om enheten är en större förpackning (fler portioner eller explicit total per förpackning). */
export function isBulkPurchaseUnit(p: CalcProduct): boolean {
  if (p.carbs_per_package != null && p.carbs_per_package > 0) return true;
  const s = p.servings_per_package;
  return s != null && s > 1 && (p.carbs ?? 0) > 0;
}

/** Bulk (pulver m.m.) — visningsetikett i tabell och prisrader. */
export const BULK_PURCHASE_UNIT_LABEL_SV = "påse/ask";
export const BULK_PURCHASE_UNIT_LABEL_PLURAL_SV = "påsar/askar";

export function unitLabelSv(p: CalcProduct): string {
  return isBulkPurchaseUnit(p) ? BULK_PURCHASE_UNIT_LABEL_SV : "st";
}

/**
 * U Gel (30 g) — föredras som standardgel om den finns i poolen (samma linje som i paketförslag).
 */
function isPreferredDefaultUGel30(p: CalcProduct): boolean {
  const n = (p.name || "").toLowerCase();
  if (!n.includes("u gel")) return false;
  const per = effectiveCarbsPerUnit(p);
  if (per > 0 && Math.abs(per - 30) <= 2) return true;
  return /\b30\s*g\b/.test(n) || /\b30g\b/.test(n.replace(/\s/g, ""));
}

/**
 * Välj produkt per kategori:
 * - **dryck:** helst större förpackningar (pulver/påse m.m.), störst kh per köp först.
 * - **gel:** helst styck; U Gel ~30 g om tillgänglig, annars närmast ~30 g kh/st.
 * - **mat:** helst styck; minst kh per köp bland styck → fler antal i förslaget.
 */
export function pickProductForCategory(
  products: CalcProduct[],
  _allocationGrams: number,
  categoryKey: NutritionCategoryKey
): CalcProduct | null {
  const usable = products.filter(isUsableForCalculator);
  if (usable.length === 0) return null;

  usable.sort((a, b) => {
    const stock = (Number(b.inStock) - Number(a.inStock)) as number;
    if (stock !== 0) return stock;

    const bulkA = isBulkPurchaseUnit(a);
    const bulkB = isBulkPurchaseUnit(b);
    const ua = effectiveCarbsPerUnit(a);
    const ub = effectiveCarbsPerUnit(b);

    if (categoryKey === "dryck") {
      if (bulkA !== bulkB) return bulkA ? -1 : 1;
      if (bulkA && bulkB) return ub - ua;
      return ub - ua;
    }

    if (categoryKey === "gel" || categoryKey === "mat") {
      if (bulkA !== bulkB) return bulkA ? 1 : -1;
      if (!bulkA && !bulkB) {
        if (categoryKey === "gel") {
          const prefA = isPreferredDefaultUGel30(a) ? 0 : 1;
          const prefB = isPreferredDefaultUGel30(b) ? 0 : 1;
          if (prefA !== prefB) return prefA - prefB;
          /** Annars: närmast ~30 g kh/st. */
          const PREFERRED = 30;
          const da = Math.abs(ua - PREFERRED);
          const db = Math.abs(ub - PREFERRED);
          if (da !== db) return da - db;
          return String(a.name || "").localeCompare(String(b.name || ""), "sv", {
            sensitivity: "base",
          });
        }
        /** Mat: fler enheter / lägre kh/st. */
        return ua - ub;
      }
      return ua - ub;
    }

    return ub - ua;
  });

  return usable[0];
}

/**
 * Kh-fördelning under loppet per sport (summa 1).
 * Regler: löpning → mest gel; cykel → sportdryck + bar, lite gel; triathlon (en tid) → balanserat.
 */
export function sportNutritionProfile(sport: EnergySport): {
  dryck: number;
  gel: number;
  mat: number;
} {
  switch (sport) {
    case "cycling":
      return { dryck: 0.58, gel: 0.12, mat: 0.3 };
    case "running":
      return { dryck: 0, gel: 0.8, mat: 0.2 };
    case "skiing":
      return { dryck: 0.38, gel: 0.32, mat: 0.3 };
    case "triathlon":
      return { dryck: 0.42, gel: 0.38, mat: 0.2 };
  }
}

/** @deprecated använd sportNutritionProfile — behålls för bakåtkompabilitet */
export function carbSharesForSport(sport: EnergySport): {
  dryck: number;
  gel: number;
  mat: number;
} {
  return sportNutritionProfile(sport);
}

/** Triathlon per gren: sim ≈ gel, cykel som ren cykel, löp som löpning */
export function sportNutritionProfileForTriathlonSegment(
  segment: TriathlonSegment
): { dryck: number; gel: number; mat: number } {
  switch (segment) {
    case "swim":
      return { dryck: 0, gel: 0.88, mat: 0.12 };
    case "bike":
      return sportNutritionProfile("cycling");
    case "run":
      return sportNutritionProfile("running");
  }
}

export type TriathlonSplitTimes = {
  swimH: number;
  swimM: number;
  bikeH: number;
  bikeM: number;
  runH: number;
  runM: number;
};

export function totalTriathlonHours(split: TriathlonSplitTimes): number {
  return (
    totalHoursFromParts(split.swimH, split.swimM) +
    totalHoursFromParts(split.bikeH, split.bikeM) +
    totalHoursFromParts(split.runH, split.runM)
  );
}

const NUTRITION_KEY_ORDER: NutritionCategoryKey[] = ["dryck", "gel", "mat"];

const NUTRITION_SLOT_ALLOWED = new Set<NutritionCategoryKey>([
  "dryck",
  "gel",
  "mat",
]);

function sanitizeNutritionSlotList(v: unknown): NutritionCategoryKey[] {
  if (!Array.isArray(v)) return [...NUTRITION_KEY_ORDER];
  const xs = v.filter((x): x is NutritionCategoryKey =>
    NUTRITION_SLOT_ALLOWED.has(x as NutritionCategoryKey)
  );
  return xs.length > 0 ? xs : [...NUTRITION_KEY_ORDER];
}

export function defaultEnergySlotConfig(): EnergySlotConfig {
  const all: NutritionCategoryKey[] = [...NUTRITION_KEY_ORDER];
  const sportNutritionSlots = {} as Record<EnergySport, NutritionCategoryKey[]>;
  for (const s of ENERGY_CALC_SPORTS) {
    sportNutritionSlots[s] = [...all];
  }
  const triathlonSegmentSlots = {} as Record<
    TriathlonSegment,
    NutritionCategoryKey[]
  >;
  for (const seg of ["swim", "bike", "run"] as TriathlonSegment[]) {
    triathlonSegmentSlots[seg] = [...all];
  }
  return { sportNutritionSlots, triathlonSegmentSlots };
}

/** Tolka API-fältet energySlotConfig */
export function normalizeEnergySlotConfig(raw: unknown): EnergySlotConfig {
  const base = defaultEnergySlotConfig();
  if (!raw || typeof raw !== "object") return base;
  const o = raw as Record<string, unknown>;
  const sports = o.sportNutritionSlots as Record<string, unknown> | undefined;
  const segs = o.triathlonSegmentSlots as Record<string, unknown> | undefined;
  const sportNutritionSlots = { ...base.sportNutritionSlots };
  for (const s of ENERGY_CALC_SPORTS) {
    if (sports && sports[s] != null) {
      sportNutritionSlots[s] = sanitizeNutritionSlotList(sports[s]);
    }
  }
  const triathlonSegmentSlots = { ...base.triathlonSegmentSlots };
  for (const seg of ["swim", "bike", "run"] as TriathlonSegment[]) {
    if (segs && segs[seg] != null) {
      const raw = segs[seg];
      /** Tom array = inga produkter för den grenen (t.ex. sim utan energiintag). */
      if (Array.isArray(raw) && raw.length === 0) {
        triathlonSegmentSlots[seg] = [];
      } else {
        triathlonSegmentSlots[seg] = sanitizeNutritionSlotList(raw);
      }
    }
  }
  return { sportNutritionSlots, triathlonSegmentSlots };
}

function orderedActiveSlots(
  activeSlots?: NutritionCategoryKey[] | null
): NutritionCategoryKey[] {
  if (activeSlots === undefined || activeSlots === null) {
    return [...NUTRITION_KEY_ORDER];
  }
  if (activeSlots.length === 0) return [];
  return NUTRITION_KEY_ORDER.filter((k) => activeSlots.includes(k));
}

export function renormalizeSharesForSlots(
  shares: { dryck: number; gel: number; mat: number },
  activeSlots: NutritionCategoryKey[]
): { dryck: number; gel: number; mat: number } {
  if (activeSlots.length === 0) {
    return { dryck: 0, gel: 0, mat: 0 };
  }
  const out = { dryck: 0, gel: 0, mat: 0 };
  let sum = 0;
  for (const k of activeSlots) {
    out[k] += shares[k];
    sum += shares[k];
  }
  if (sum < 0.0001) return out;
  for (const k of activeSlots) {
    out[k] /= sum;
  }
  return out;
}

export function filterCatalogByNutritionSlots(
  catalog: EnergyCatalog,
  activeSlots: NutritionCategoryKey[]
): EnergyCatalog {
  const set = new Set(activeSlots);
  return {
    ...catalog,
    dryck: set.has("dryck") ? catalog.dryck : [],
    gel: set.has("gel") ? catalog.gel : [],
    mat: set.has("mat") ? catalog.mat : [],
  };
}

export function nutritionSlotsForSport(
  config: EnergySlotConfig | null | undefined,
  sport: EnergySport
): NutritionCategoryKey[] {
  if (!config) return [...NUTRITION_KEY_ORDER];
  return sanitizeNutritionSlotList(config.sportNutritionSlots[sport]);
}

export function nutritionSlotsForTriathlonSegment(
  config: EnergySlotConfig | null | undefined,
  segment: TriathlonSegment
): NutritionCategoryKey[] {
  if (!config) return [...NUTRITION_KEY_ORDER];
  const seg = config.triathlonSegmentSlots[segment];
  if (seg != null) {
    if (seg.length === 0) return [];
    return sanitizeNutritionSlotList(seg);
  }
  return nutritionSlotsForSport(config, "triathlon");
}

function buildPackageLinesWithShares(
  catalog: EnergyCatalog,
  targetCarbsDuring: number,
  shares: { dryck: number; gel: number; mat: number },
  options?: { segment?: TriathlonSegment; activeSlots?: NutritionCategoryKey[] }
): PackageLine[] {
  const keys = orderedActiveSlots(options?.activeSlots);
  const shareNorm = renormalizeSharesForSlots(shares, keys);
  const lines: PackageLine[] = [];
  const segPrefix: Record<TriathlonSegment, string> = {
    swim: "Sim · ",
    bike: "Cykel · ",
    run: "Löpning · ",
  };
  const prefix = options?.segment ? segPrefix[options.segment] : "";

  for (const key of keys) {
    const share = shareNorm[key];
    if (share < 0.001) continue;
    const allocation = Math.round(targetCarbsDuring * share);
    if (allocation < 1) continue;
    const product = pickProductForCategory(catalog[key], allocation, key);
    if (!product) continue;
    const perUnit = effectiveCarbsPerUnit(product);
    const units = perUnit > 0 ? Math.ceil(allocation / perUnit) : 0;
    if (units <= 0) continue;
    const carbsDelivered = Math.round(units * perUnit);
    const lineId = options?.segment ? `${options.segment}-${key}` : key;
    lines.push({
      categoryKey: key,
      categoryLabel: `${prefix}${CATEGORY_LABELS[key]}`,
      product,
      units,
      carbsDelivered,
      allocationTarget: allocation,
      lineId,
      segment: options?.segment,
    });
  }

  return lines;
}

/**
 * Triathlon med separata tider: olika produktprofiler per gren (sim/cykel/löp).
 */
export function buildTriathlonSegmentPackageLines(
  catalog: EnergyCatalog,
  carbsPerHour: number,
  split: TriathlonSplitTimes
): PackageLine[] {
  const swimH = totalHoursFromParts(split.swimH, split.swimM);
  const bikeH = totalHoursFromParts(split.bikeH, split.bikeM);
  const runH = totalHoursFromParts(split.runH, split.runM);

  const parts: { segment: TriathlonSegment; hours: number }[] = [
    { segment: "swim", hours: swimH },
    { segment: "bike", hours: bikeH },
    { segment: "run", hours: runH },
  ];

  const lines: PackageLine[] = [];
  for (const { segment, hours } of parts) {
    if (hours <= 0) continue;
    const target = Math.round(hours * carbsPerHour);
    if (target < 1) continue;
    const shares = sportNutritionProfileForTriathlonSegment(segment);
    const slots = nutritionSlotsForTriathlonSegment(
      catalog.energySlotConfig,
      segment
    );
    const filtered = filterCatalogByNutritionSlots(catalog, slots);
    lines.push(
      ...buildPackageLinesWithShares(filtered, target, shares, {
        segment,
        activeSlots: slots,
      })
    );
  }
  return lines;
}

function sportEnergyGuidance(
  sport: EnergySport,
  triathlonSplitMode?: boolean
): string {
  if (sport === "triathlon" && triathlonSplitMode) {
    return "Triathlon (gren för gren): under sim ingår inga energiprodukter i förslaget om simgrenen har tom slot-lista (ingen konsumtion under vattnet); cykel och löpning följer dina valda kategorier per gren.";
  }
  switch (sport) {
    case "triathlon":
      return "Triathlon: vätska på cykeln; gel på löpning — med en gemensam tid följer en balanserad fördelning.";
    case "running":
      return "Löpning: förslaget prioriterar gel; lite bar. Nästan ingen sportdryck i tabellen (ta vätska från stationer eller egen flaska).";
    case "cycling":
      return "Cykel: förslaget prioriterar sportdryck och bar; lite gel mellan depåer.";
    case "skiing":
      return "Längd: tänk på vätska även i kyla; gel och bar i bälte eller ficka, lättillgängligt.";
  }
}

export type PackageLine = {
  categoryKey: keyof EnergyCatalog;
  categoryLabel: string;
  product: CalcProduct;
  units: number;
  carbsDelivered: number;
  allocationTarget: number;
  /** Flaska / tillbehör utan kh i översikten */
  accessory?: boolean;
  /** Unik rad vid triathlon med flera grenar */
  lineId?: string;
  /** Triathlon: vilken gren raden avser */
  segment?: TriathlonSegment;
  /** Före start: kort intagsinstruktion (visas i produktraden) */
  preraceIntakeHint?: string;
};

/** Fördela heltal jämnt; rest fördelas på de första indexen. */
export function distributeEvenInt(total: number, n: number): number[] {
  if (n <= 0) return [];
  if (total <= 0) return Array(n).fill(0);
  const base = Math.floor(total / n);
  const rem = total - base * n;
  return Array.from({ length: n }, (_, i) => base + (i < rem ? 1 : 0));
}

/** `gel-u3` → `gel`; `run-gel-u0` → `run-gel` (bas före `-u{n}`). */
export function parentBaseIdFromExpandedLineId(lineId: string): string {
  const m = lineId.match(/^(.+)-u\d+$/);
  return m ? m[1]! : lineId;
}

/** Grupperar expanderade gel-/bar-rader som hör till samma ursprungliga fördelning (samma kh-pool). */
export function gelMatPackageGroupKey(line: PackageLine): string | null {
  if (line.accessory || line.preraceIntakeHint != null) return null;
  if (line.categoryKey !== "gel" && line.categoryKey !== "mat") return null;
  const lid = line.lineId;
  if (!lid) return null;
  const base = parentBaseIdFromExpandedLineId(lid);
  return `${base}|${line.categoryKey}|${line.segment ?? ""}`;
}

function chosenProductIdForRebalanceLine(
  line: PackageLine,
  flavorChoicesByLineKey: Record<string, number>
): number {
  const k = packageLineFlavorGroupKey(line);
  const c = flavorChoicesByLineKey[k];
  return c != null ? c : line.product.id;
}

/**
 * Efter expand: om alla geler/bar i samma grupp har samma valda produkt, räkna om antal rader
 * utifrån samlat kh-mål för gruppen och kh/st (undvik per-rad avrundning som ger fel total).
 * Blandade produkter inom gruppen lämnas orörda (löses som tidigare per rad).
 */
export function rebalanceGelMatExpandedPackageLines(
  lines: PackageLine[],
  flavorChoicesByLineKey: Record<string, number>,
  catalog: EnergyCatalog
): PackageLine[] {
  const out: PackageLine[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    const gk = gelMatPackageGroupKey(line);
    if (!gk) {
      out.push(line);
      i++;
      continue;
    }
    let j = i + 1;
    while (j < lines.length && gelMatPackageGroupKey(lines[j]!) === gk) {
      j++;
    }
    const slice = lines.slice(i, j);
    out.push(...rebalanceOneGelMatGroup(slice, flavorChoicesByLineKey, catalog));
    i = j;
  }
  return out;
}

function rebalanceOneGelMatGroup(
  slice: PackageLine[],
  flavorChoicesByLineKey: Record<string, number>,
  catalog: EnergyCatalog
): PackageLine[] {
  if (slice.length === 0) return slice;
  const ids = slice.map((l) => chosenProductIdForRebalanceLine(l, flavorChoicesByLineKey));
  const unique = new Set(ids);
  if (unique.size !== 1) {
    return slice;
  }
  const productId = ids[0]!;
  const product = findProductInEnergyCatalog(catalog, productId);
  if (!product || !isUsableForCalculator(product)) {
    return slice;
  }
  const per = effectiveCarbsPerUnit(product);
  if (per <= 0) {
    return slice;
  }
  const totalAlloc = slice.reduce((s, l) => s + l.allocationTarget, 0);
  if (totalAlloc < 1) {
    return slice;
  }
  const newUnits = Math.max(1, Math.ceil(totalAlloc / per));
  const allocParts = distributeEvenInt(totalAlloc, newUnits);
  const totalCarbs = Math.round(newUnits * per);
  const carbParts = distributeEvenInt(totalCarbs, newUnits);
  const first = slice[0]!;
  const baseId =
    first.lineId != null
      ? parentBaseIdFromExpandedLineId(first.lineId)
      : `${String(first.categoryKey)}-${first.product.id}`;
  return Array.from({ length: newUnits }, (_, k) => ({
    ...first,
    product,
    units: 1,
    allocationTarget: allocParts[k] ?? 0,
    carbsDelivered: carbParts[k] ?? 0,
    /** En rad: samma nyckel som vid aggregerad enhet (expand lägger bara till `-u{n}` när flera). */
    lineId: newUnits === 1 ? baseId : `${baseId}-u${k}`,
  }));
}

/**
 * En tabellrad per köpt enhet (st eller påse/ask) så smak kan väljas per gel, bar, dryck osv.
 * Tillbehör med flera identiska enheter (t.ex. flaskor) lämnas som en rad.
 */
export function expandPackageLinesToSingleUnits(lines: PackageLine[]): PackageLine[] {
  const out: PackageLine[] = [];
  for (const line of lines) {
    if (line.accessory || line.units <= 1) {
      out.push(line);
      continue;
    }
    const n = line.units;
    const allocParts = distributeEvenInt(line.allocationTarget, n);
    const carbParts = distributeEvenInt(line.carbsDelivered, n);
    const baseId =
      line.lineId ?? `${String(line.categoryKey)}-${line.product.id}`;
    for (let i = 0; i < n; i++) {
      out.push({
        ...line,
        units: 1,
        allocationTarget: allocParts[i] ?? 0,
        carbsDelivered: carbParts[i] ?? 0,
        lineId: `${baseId}-u${i}`,
      });
    }
  }
  return out;
}

/**
 * Efter produktbyte kan gel/bar/dryck få flera enheter på en rad. Flaskor/tillbehör får ligga kvar
 * med flera st per rad. Övriga kategorier splittas till en rad per enhet (samma smakgruppnyckel via
 * `packageLineFlavorGroupKey`).
 */
export function splitNonBottlePackageLinesToSingleUnitRows(
  combined: PackageLine[],
  resolved: PackageLine[]
): { combined: PackageLine[]; resolved: PackageLine[] } {
  if (combined.length !== resolved.length) {
    return { combined: [...combined], resolved: [...resolved] };
  }
  const outC: PackageLine[] = [];
  const outR: PackageLine[] = [];
  for (let i = 0; i < combined.length; i++) {
    const c = combined[i]!;
    const r = resolved[i]!;
    const shouldSplit =
      !r.accessory &&
      r.preraceIntakeHint == null &&
      r.categoryKey !== "flaskor" &&
      r.units > 1;
    if (!shouldSplit) {
      outC.push(c);
      outR.push(r);
      continue;
    }
    const n = r.units;
    const allocC = distributeEvenInt(c.allocationTarget, n);
    const carbC = distributeEvenInt(c.carbsDelivered, n);
    const allocR = distributeEvenInt(r.allocationTarget, n);
    const carbR = distributeEvenInt(r.carbsDelivered, n);
    const baseIdC = c.lineId ?? `${String(c.categoryKey)}-${c.product.id}`;
    const baseIdR = r.lineId ?? `${String(r.categoryKey)}-${r.product.id}`;
    for (let j = 0; j < n; j++) {
      outC.push({
        ...c,
        units: 1,
        allocationTarget: allocC[j] ?? 0,
        carbsDelivered: carbC[j] ?? 0,
        lineId: `${baseIdC}-split${j}`,
      });
      outR.push({
        ...r,
        units: 1,
        allocationTarget: allocR[j] ?? 0,
        carbsDelivered: carbR[j] ?? 0,
        lineId: `${baseIdR}-split${j}`,
      });
    }
  }
  return { combined: outC, resolved: outR };
}

const CATEGORY_LABELS: Record<
  NutritionCategoryKey | "flaskor" | "prerace",
  string
> = {
  dryck: "Sportdryck",
  gel: "Gel",
  mat: "Bar",
  flaskor: "Flaska / vätskesystem",
  prerace: "Före start",
};

/** Måste matcha id för "Vätternrundan 315 km" i `energyCalculatorDistances.ts`. */
export const CYCLING_VATTERN_315_PRESET_ID = "vattern315";

function legacyRecommendedBottleCount(args: {
  hoursTotal: number;
  distanceKm: number | null;
  sport: EnergySport;
}): number {
  const h = Math.max(0, args.hoursTotal);
  if (h <= 0) return 0;
  let fromTime = 1;
  switch (args.sport) {
    case "cycling":
      fromTime = Math.ceil(h * 0.5);
      break;
    case "triathlon":
      fromTime = Math.ceil(h * 0.42);
      break;
    case "running":
      fromTime = Math.max(1, Math.ceil(h * 0.22));
      break;
    case "skiing":
      fromTime = Math.max(1, Math.ceil(h * 0.35));
      break;
  }
  let fromDist = 0;
  if (args.distanceKm != null && args.distanceKm > 0) {
    if (args.sport === "cycling") {
      fromDist = Math.ceil(args.distanceKm / 50);
    } else if (args.sport === "triathlon") {
      fromDist = Math.ceil(args.distanceKm / 70);
    }
  }
  const n = Math.max(fromTime, fromDist, 1);
  return Math.min(12, n);
}

/**
 * Antal flaskor utifrån tid och (för cykel/triathlon) distans.
 *
 * **Cykel:** Under 7 h eller annat än Vätternrundan 315 km → 2 flaskor. Vättern 315 km med minst 7 h → som tidigare
 * riktlinje men max 6. Övriga sporter: oförändrad heuristik.
 */
export function recommendedBottleCount(args: {
  hoursTotal: number;
  distanceKm: number | null;
  sport: EnergySport;
  /** Endast cykel: vald distansförinställning (t.ex. `vattern315`). */
  cyclingDistancePresetId?: string | null;
}): number {
  const h = Math.max(0, args.hoursTotal);
  if (h <= 0) return 0;

  if (args.sport === "cycling") {
    if (h < 7) {
      return 2;
    }
    if (args.cyclingDistancePresetId === CYCLING_VATTERN_315_PRESET_ID) {
      return Math.min(6, legacyRecommendedBottleCount(args));
    }
    return 2;
  }

  return legacyRecommendedBottleCount(args);
}

/** Standardflaska i energikalkylatorn om artikeln finns i poolen. */
export const DEFAULT_BOTTLE_PRODUCT_ID = 7683;

export function pickBottleProduct(
  products: CalcProduct[],
  preferredId?: number | null
): CalcProduct | null {
  if (products.length === 0) return null;
  const want =
    preferredId != null && Number.isFinite(Number(preferredId))
      ? Number(preferredId)
      : DEFAULT_BOTTLE_PRODUCT_ID;
  const preferred = products.find((p) => p.id === want);
  if (preferred) return preferred;
  if (want !== DEFAULT_BOTTLE_PRODUCT_ID) {
    const legacy = products.find((p) => p.id === DEFAULT_BOTTLE_PRODUCT_ID);
    if (legacy) return legacy;
  }
  const sorted = [...products].sort((a, b) => {
    const stock = Number(b.inStock) - Number(a.inStock);
    if (stock !== 0) return stock;
    return String(a.name || "").localeCompare(String(b.name || ""), "sv", {
      sensitivity: "base",
    });
  });
  return sorted[0];
}

/** Sant om paketrader under loppet innehåller sportdryck (inte prerace). */
export function packageLinesIncludeSportDryck(lines: PackageLine[]): boolean {
  return lines.some(
    (l) => l.categoryKey === "dryck" && l.preraceIntakeHint == null
  );
}

export function buildBottlePackageLine(
  catalog: EnergyCatalog,
  bottleCount: number
): PackageLine | null {
  if (bottleCount <= 0) return null;
  const product = pickBottleProduct(
    catalog.flaskor,
    catalog.defaultBottleProductId
  );
  if (!product) return null;
  return {
    categoryKey: "flaskor",
    categoryLabel: CATEGORY_LABELS.flaskor,
    product,
    units: bottleCount,
    carbsDelivered: 0,
    allocationTarget: 0,
    accessory: true,
    lineId: "flaskor",
  };
}

export function buildPackageLines(
  catalog: EnergyCatalog,
  targetCarbsDuring: number,
  sport: EnergySport
): PackageLine[] {
  const slots = nutritionSlotsForSport(catalog.energySlotConfig, sport);
  const filtered = filterCatalogByNutritionSlots(catalog, slots);
  const shares = sportNutritionProfile(sport);
  return buildPackageLinesWithShares(filtered, targetCarbsDuring, shares, {
    activeSlots: slots,
  });
}

export function sumDeliveredCarbs(lines: PackageLine[]): number {
  return lines.reduce((s, l) => s + l.carbsDelivered, 0);
}

export function totalHoursFromParts(hours: number, minutes: number): number {
  const h = Math.max(0, hours);
  const m = Math.max(0, Math.min(59, minutes));
  return h + m / 60;
}

export function totalHoursFromHms(
  hours: number,
  minutes: number,
  seconds: number
): number {
  const h = Math.max(0, hours);
  const m = Math.max(0, Math.min(59, minutes));
  const s = Math.max(0, Math.min(59, seconds));
  return h + m / 60 + s / 3600;
}

export function targetCarbsDuringRace(
  hoursTotal: number,
  carbsPerHour: number
): number {
  if (hoursTotal <= 0 || carbsPerHour <= 0) return 0;
  return Math.round(hoursTotal * carbsPerHour);
}

/** Raceload ca 12–18 % av kh under loppet (riktlinje, ingen kroppsvikt i v1). */
export function suggestedPreloadCarbs(targetDuring: number): number {
  if (targetDuring <= 0) return 0;
  return Math.round(targetDuring * 0.15);
}

/** Standardval mg koffein per kg kroppsvikt före start (kan ändras 3–9 i UI). */
export const DEFAULT_PRERACE_MG_PER_KG = 4;
export const PRERACE_MG_PER_KG_MIN = 3;
export const PRERACE_MG_PER_KG_MAX = 9;

/** @deprecated använd DEFAULT_PRERACE_MG_PER_KG (ett värde, inte min/max-objekt) */
export const DEFAULT_PRERACE_CAFFEINE_MG_PER_KG = {
  min: DEFAULT_PRERACE_MG_PER_KG,
  max: DEFAULT_PRERACE_MG_PER_KG,
};

/** @deprecated */
export const PRERACE_CAFFEINE_MG_PER_KG = DEFAULT_PRERACE_CAFFEINE_MG_PER_KG;

/**
 * Riktlinje mg koffein totalt före start utifrån vikt och valt mg/kg.
 * Returnerar { min, max } med samma värde (för bakåtkompabilitet med tabelltexter).
 */
export function preraceCaffeineMgRange(
  weightKg: number,
  mgPerKg: number = DEFAULT_PRERACE_MG_PER_KG
): { min: number; max: number } | null {
  if (!Number.isFinite(weightKg) || weightKg <= 0 || weightKg > 400) {
    return null;
  }
  if (!Number.isFinite(mgPerKg) || mgPerKg <= 0) {
    return null;
  }
  const t = Math.round(weightKg * mgPerKg);
  return { min: t, max: t };
}

/** WooCommerce-produkt-ID:n för fast före start-upplägg i energikalkylatorn. */
export const PRERACE_PRODUCT_NITRATE_ID = 7627;
export const PRERACE_PRODUCT_CAFFEINE_SHOT_ID = 11736;
export const PRERACE_PRODUCT_CAFFEINE_EXTRA_ID = 15308;

/** Riktlinje mg (t.ex. 320) avrundas nedåt till jämna 50 mg för tablettantal — visning kan fortfarande säga 320. */
export const PRERACE_CAFFEINE_TABLET_ROUND_STEP_MG = 50;

export function floorMgToPreraceTabletStep(
  mg: number,
  stepMg: number = PRERACE_CAFFEINE_TABLET_ROUND_STEP_MG
): number {
  if (!Number.isFinite(mg) || mg <= 0) return 0;
  return Math.floor(mg / stepMg) * stepMg;
}

export type PreraceCaffeineTabletRecommendation = {
  mgPer: number;
  rawLo: number;
  rawHi: number;
  /** Visningsriktlinje (t.ex. ca 320 mg); tabletter bygger på avrundat 50-steg. */
  mgTargetLabel: string;
};

/**
 * Samma tablettintervall som i produktradens koffeinhint (jämna 50 mg-steg nedåt mot riktlinjen).
 */
export function getPreraceCaffeineTabletRecommendation(
  product: CalcProduct,
  bodyWeightKg: number | null,
  priorCaffeineMgFromOtherPreraceLines: number,
  preraceMgPerKg: number = DEFAULT_PRERACE_MG_PER_KG
): PreraceCaffeineTabletRecommendation | null {
  const mgPer = product.caffeine_mg_per_serving;
  const hasCaf =
    productCaffeineKind(product) === "contains" &&
    mgPer != null &&
    Number.isFinite(mgPer) &&
    mgPer > 0;

  if (!hasCaf) return null;
  if (product.id === PRERACE_PRODUCT_CAFFEINE_SHOT_ID) return null;

  const r =
    bodyWeightKg != null
      ? preraceCaffeineMgRange(bodyWeightKg, preraceMgPerKg)
      : null;
  if (r == null) return null;

  const step = PRERACE_CAFFEINE_TABLET_ROUND_STEP_MG;
  const budgetMin = floorMgToPreraceTabletStep(r.min, step);
  const budgetMax = floorMgToPreraceTabletStep(r.max, step);
  const remMin = Math.max(0, budgetMin - priorCaffeineMgFromOtherPreraceLines);
  const remMax = Math.max(0, budgetMax - priorCaffeineMgFromOtherPreraceLines);

  if (remMax <= 0) return null;

  const rawLo = remMin <= 0 ? 0 : Math.ceil(remMin / mgPer);
  const rawHi = Math.ceil(remMax / mgPer);
  if (rawHi < 1) return null;

  const mgTargetLabel =
    r.min === r.max ? `ca ${r.min} mg` : `ca ${r.min}–${r.max} mg`;

  return { mgPer, rawLo, rawHi, mgTargetLabel };
}

/**
 * När riktlinjen (mg totalt) överstiger detta föreslås extra koffeinprodukt.
 */
export const PRERACE_EXTRA_CAFFEINE_THRESHOLD_MG = 250;

export function shouldShowExtraPreraceCaffeineProduct(
  bodyWeightKg: number | null,
  mgPerKg: number = DEFAULT_PRERACE_MG_PER_KG
): boolean {
  const r =
    bodyWeightKg != null ? preraceCaffeineMgRange(bodyWeightKg, mgPerKg) : null;
  return r != null && r.max > PRERACE_EXTRA_CAFFEINE_THRESHOLD_MG;
}

function findProductByIdInEnergyCatalog(
  catalog: EnergyCatalog,
  id: number
): CalcProduct | null {
  return findProductInEnergyCatalog(catalog, id);
}

/**
 * Fasta före start-rader: nitrat (7627) alltid; vid koffein i paketet även koffeinshot (11736) och ev. extra koffein (15308).
 * Vid filter ”Utan koffein” lämnas koffeinprodukter bort.
 * Söker produkt i prerace-poolen först, sedan övriga kategorier.
 */
export function buildPreracePackageLines(
  catalog: EnergyCatalog,
  bodyWeightKg: number | null,
  preraceMgPerKg: number = DEFAULT_PRERACE_MG_PER_KG,
  caffeineMode: CaffeineFilterMode = "contains"
): PackageLine[] {
  const lines: PackageLine[] = [];
  const includeCaffeinePrerace = caffeineMode === "contains";

  const push = (
    productId: number,
    lineId: string,
    intakeHint: string
  ) => {
    const product = findProductByIdInEnergyCatalog(catalog, productId);
    if (!product) return;
    const perUnit = effectiveCarbsPerUnit(product);
    const units = 1;
    const carbsDelivered = Math.round(units * perUnit);
    lines.push({
      categoryKey: "prerace",
      categoryLabel: CATEGORY_LABELS.prerace,
      product,
      units,
      carbsDelivered,
      allocationTarget: carbsDelivered,
      lineId,
      preraceIntakeHint: intakeHint,
    });
  };

  push(
    PRERACE_PRODUCT_NITRATE_ID,
    "prerace-nitrate",
    "ca 2,5 h före start"
  );
  if (!includeCaffeinePrerace) {
    return lines;
  }
  push(
    PRERACE_PRODUCT_CAFFEINE_SHOT_ID,
    "prerace-caffeine-base",
    "ca 0,5 h före start"
  );
  if (shouldShowExtraPreraceCaffeineProduct(bodyWeightKg, preraceMgPerKg)) {
    push(
      PRERACE_PRODUCT_CAFFEINE_EXTRA_ID,
      "prerace-caffeine-extra",
      "ca 0,5 h före start"
    );
  }

  return lines;
}

/**
 * Summerar koffein (mg) från tidigare rader i samma tabell som har `preraceIntakeHint`
 * (före `beforeIndex`). Används för att räkna ut tabletter för nästa produkt.
 */
export function sumPreraceCaffeineMgBeforeIndex(
  lines: PackageLine[],
  beforeIndex: number,
  /** Om satt: räkna bara rader där detta är sant (t.ex. bortvalda rader räknas inte). */
  includeLineAtIndex?: (index: number) => boolean
): number {
  let sum = 0;
  for (let i = 0; i < beforeIndex && i < lines.length; i++) {
    if (includeLineAtIndex && !includeLineAtIndex(i)) continue;
    const line = lines[i];
    if (line.preraceIntakeHint == null) continue;
    const p = line.product;
    if (productCaffeineKind(p) !== "contains") continue;
    const mg = p.caffeine_mg_per_serving;
    if (mg == null || !Number.isFinite(mg) || mg <= 0) continue;
    const u = Math.max(1, line.units);
    sum += mg * u;
  }
  return sum;
}

/**
 * Kort svensk vägledning för koffein före start: riktlinje mg + antal tabletter
 * (för t.ex. U Caffeine) efter att shot m.m. räknats av.
 */
export function preraceCaffeineDosingHintSv(
  product: CalcProduct,
  bodyWeightKg: number | null,
  priorCaffeineMgFromOtherPreraceLines: number,
  preraceMgPerKg: number = DEFAULT_PRERACE_MG_PER_KG
): string | null {
  const mgPer = product.caffeine_mg_per_serving;
  const hasCaf =
    productCaffeineKind(product) === "contains" &&
    mgPer != null &&
    Number.isFinite(mgPer) &&
    mgPer > 0;

  if (!hasCaf) return null;

  const r =
    bodyWeightKg != null
      ? preraceCaffeineMgRange(bodyWeightKg, preraceMgPerKg)
      : null;

  if (r == null) {
    return `~${Math.round(mgPer)} mg koffein per portion. Ange vikt under ”Om dig” för att se riktlinjens mg-spann.`;
  }

  const mgTargetLabel =
    r.min === r.max ? `ca ${r.min} mg` : `ca ${r.min}–${r.max} mg`;

  if (product.id === PRERACE_PRODUCT_CAFFEINE_SHOT_ID) {
    return `Riktlinje vid din vikt: ${mgTargetLabel} koffein totalt före start. Denna produkt ger ~${Math.round(mgPer)} mg per portion.`;
  }

  const step = PRERACE_CAFFEINE_TABLET_ROUND_STEP_MG;
  const budgetMin = floorMgToPreraceTabletStep(r.min, step);
  const budgetMax = floorMgToPreraceTabletStep(r.max, step);
  const remMin = Math.max(0, budgetMin - priorCaffeineMgFromOtherPreraceLines);
  const remMax = Math.max(0, budgetMax - priorCaffeineMgFromOtherPreraceLines);

  if (remMax <= 0) {
    return `Riktlinjen (${mgTargetLabel} totalt) är redan täckt av produkterna ovan i listan.`;
  }

  const rawLo = remMin <= 0 ? 0 : Math.ceil(remMin / mgPer);
  const rawHi = Math.ceil(remMax / mgPer);

  if (rawHi < 1) return null;

  const unitWord = "tabletter";

  if (rawLo === 0) {
    return `För att tillsammans med raderna ovan ligga inom riktlinjen ${mgTargetLabel} totalt: högst ca ${rawHi} ${unitWord} à ~${Math.round(mgPer)} mg.`;
  }
  if (rawLo === rawHi) {
    return `För att tillsammans med raderna ovan ligga inom riktlinjen ${mgTargetLabel} totalt: ca ${rawLo} ${unitWord} à ~${Math.round(mgPer)} mg.`;
  }
  return `För att tillsammans med raderna ovan ligga inom riktlinjen ${mgTargetLabel} totalt: ca ${rawLo}–${rawHi} ${unitWord} à ~${Math.round(mgPer)} mg.`;
}

export type IntakePhase = {
  heading: string;
  summary: string;
  bullets: string[];
};

export function buildIntakePlan(args: {
  distanceKm: number | null;
  hoursTotal: number;
  carbsPerHour: number;
  targetDuring: number;
  preloadCarbs: number;
  sport: EnergySport;
  caffeineMode: CaffeineFilterMode;
  includeBottles?: boolean;
  bottleCount?: number;
  /** Triathlon: uppdelade tider per gren */
  triathlonSplitMode?: boolean;
  /** Kroppsvikt (kg) — används till koffeinvägledning före start */
  bodyWeightKg?: number | null;
  /** mg koffein per kg kroppsvikt före start (samma som i energipaket) */
  preraceMgPerKg?: number;
}): { title: string; phases: IntakePhase[] } {
  const {
    distanceKm,
    hoursTotal,
    carbsPerHour,
    targetDuring,
    preloadCarbs,
    sport,
    caffeineMode,
    includeBottles,
    bottleCount,
    triathlonSplitMode,
    bodyWeightKg,
    preraceMgPerKg = DEFAULT_PRERACE_MG_PER_KG,
  } = args;
  const shares = sportNutritionProfile(sport);
  const bottleBullet =
    includeBottles && bottleCount != null && bottleCount > 0
      ? `Förslaget kan inkludera ${bottleCount} st flaska eller vätskesystem — ungefärligt behov utifrån tid${distanceKm != null && distanceKm > 0 ? " och angiven distans" : ""} (anpassa efter ram, depåer och väder).`
      : null;

  const distPart =
    distanceKm != null && distanceKm > 0
      ? `${formatNum(distanceKm)} km`
      : "ditt lopp";

  const timeStr = formatDurationSv(hoursTotal);
  const cafPre =
    bodyWeightKg != null &&
    Number.isFinite(bodyWeightKg) &&
    bodyWeightKg > 0
      ? preraceCaffeineMgRange(bodyWeightKg, preraceMgPerKg)
      : null;
  const preRaceCaffeineBullet =
    caffeineMode === "contains" &&
    cafPre != null &&
    bodyWeightKg != null
      ? `Med ditt val (${preraceMgPerKg} mg/kg och vikt ${formatNum(bodyWeightKg)} kg) motsvarar riktlinjen ungefär ${cafPre.min} mg koffein totalt före start — planera med produkterna under ”Före start” i tabellen nedan.`
      : null;

  return {
    title: "Energiplan (riktlinje)",
    phases: [
      {
        heading: "Före start — raceload",
        summary: `Ladda glykogendepåer 2–4 timmar innan. Som riktlinje motsvarar det ofta ungefär ${preloadCarbs} g kolhydrat utöver det du äter till vardags — fördelat på måltid + dryck du tål.`,
        bullets: [
          "Undvik att experimentera med nya produkter på tävlingsdagen.",
          "Drick regelbundet; salt kan behövas om det är varmt eller lång tid till start.",
          ...(preRaceCaffeineBullet ? [preRaceCaffeineBullet] : []),
        ],
      },
      {
        heading: "Under loppet",
        summary: `${ENERGY_SPORT_LABELS[sport]}: ${distPart} på cirka ${timeStr} med mål ${carbsPerHour} g kolhydrater per timme ger ungefär ${targetDuring} g kolhydrat under själva loppet.`,
        bullets: [
          sportEnergyGuidance(sport, triathlonSplitMode),
          ...(bottleBullet ? [bottleBullet] : []),
          ...(sport === "triathlon" && triathlonSplitMode
            ? [
                "Med uppdelade tider: sim använder mest gel, cykel följer dryck + bar som på ren cykel, löpning följer gel/bar som på ren löpning. Varje gren får eget produktförslag.",
              ]
            : []),
          ...(sport === "triathlon" && triathlonSplitMode
            ? []
            : [
                `Utifrån vald sport fördelas målkh ungefär ${Math.round(shares.dryck * 100)} % dryck, ${Math.round(shares.gel * 100)} % gel och ${Math.round(shares.mat * 100)} % bar i paketförslaget — justera efter vad du tål.`,
              ]),
          "I paketförslaget prioriteras styck gel och bar; sportdryck väljs gärna som större förpackning (t.ex. pulver) när sortimentet tillåter.",
          "Ta små, jämna muggar av vätska och energi i stället för sällan stora klunkar.",
          "Vid värme: extra vätska och salt; sänk kh-takt om magen protesterar.",
        ],
      },
      {
        heading: "Efter mål",
        summary:
          "Återhämtning: vätska, kolhydrater och lite protein inom 1–2 timmar.",
        bullets: [
          "En måltid du känner igen slår oftast exotiska produkter här.",
        ],
      },
    ],
  };
}

/** Visuell intagsplan (tidslinje) — byggs i klienten från paketrader och målvärden. */
export type IntakeTimelinePreraceRow = {
  minutesBeforeStart: number;
  timeLabel: string;
  productName: string;
  units: number;
  /** Om satt: ersätter "{units} st" (t.ex. "2 tabletter" i linje med produktradens koffeinhint). */
  preraceQuantityLabel?: string | null;
  carbsG: number;
  caffeineMg: number | null;
  isNitrate: boolean;
  image: string | null;
};

export type IntakeTimelineRaceItem =
  | {
      kind: "gel_tick";
      minutesFromStart: number;
      timeLabel: string;
      lineSummary: string;
      image: string | null;
      /** Kolhydrater i en hel gel — används för grön "X g kh"-tagg. */
      carbsG: number;
    }
  | {
      kind: "cycling_hour";
      hourIndex: number;
      timeLabel: string;
      summary: string;
      /** Från paketförslaget (första rad per kategori), samma för alla timmar. */
      barImage: string | null;
      dryckImage: string | null;
      gelImage: string | null;
      /**
       * Om timmen rymmer minst en hel bar (≥ ~1 portion) sätter vi
       * kolhydratmängden här så UI kan visa grön kh-tagg. Null när bars delas
       * mellan flera timmar (t.ex. ½ bar per timme).
       */
      wholeBarCarbsG: number | null;
    }
  | {
      kind: "triathlon_segment_header";
      segment: TriathlonSegment;
      /** "Sim" / "Cykel" / "Löpning" */
      label: string;
      /** När i loppet grenen börjar, t.ex. "start", "45 min", "1 h 30 min". */
      startTimeLabel: string;
      /** Varaktighet för grenen (t.ex. "45 min"). */
      durationLabel: string;
      /** Kort riktlinje för grenen. */
      summary: string;
    }
  | { kind: "triathlon_split_note"; summary: string }
  | { kind: "race_note"; summary: string };

export type IntakeTimelineData = {
  stats: {
    durationLabel: string;
    carbsPerHour: number;
    totalCarbs: number;
    caffeineMgTotal: number;
  };
  prerace: IntakeTimelinePreraceRow[];
  raceItems: IntakeTimelineRaceItem[];
  /** Bara satt för cykling — används av flask-/depåplaneraren under timeline. */
  cyclingPlan: {
    totalDryckG: number;
    perHourDryckG: number;
  } | null;
  notes: {
    intro: string;
    preload: string;
    afterGoal: string;
  };
};

function parsePreraceHintMinutesBeforeStart(hint: string): number {
  const normalized = hint.replace(/\s/g, " ").replace(",", ".");
  const m = normalized.match(/(\d+(?:\.\d+)?)\s*h/i);
  if (m) return Math.round(parseFloat(m[1]!) * 60);
  return 0;
}

function formatTimeBeforeStartLabel(minutesBeforeStart: number): string {
  if (minutesBeforeStart <= 0) return "start";
  const h = Math.floor(minutesBeforeStart / 60);
  const m = Math.round(minutesBeforeStart % 60);
  const parts: string[] = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  return parts.length > 0 ? `-${parts.join(" ")}` : "0";
}

function formatMinutesFromRaceStart(minutesFromStart: number): string {
  if (minutesFromStart < 60) {
    return `${Math.round(minutesFromStart)} min`;
  }
  const h = Math.floor(minutesFromStart / 60);
  const m = Math.round(minutesFromStart % 60);
  if (m <= 0) return `${h} h`;
  return `${h} h ${m} min`;
}

const INTAKE_TIMELINE_MAX_GEL_TICKS = 24;

const TRIATHLON_SEGMENT_LABEL_SV: Record<TriathlonSegment, string> = {
  swim: "Sim",
  bike: "Cykel",
  run: "Löpning",
};

function formatDurationShortSv(hours: number): string {
  const totalMin = Math.max(0, Math.round(hours * 60));
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m > 0 ? `${h} h ${m} min` : `${h} h`;
}

/**
 * True om timmens bar-intag motsvarar ungefär en hel bar eller mer (dvs. en hel
 * portion konsumeras den timmen). Används för att veta när vi kan visa en
 * "X g kh"-tagg (grön) — annars delas baren upp över flera timmar.
 */
function isWholeBarPerHour(
  barG: number,
  barCarbsPerPortion: number | null | undefined
): boolean {
  if (
    barCarbsPerPortion == null ||
    !Number.isFinite(barCarbsPerPortion) ||
    barCarbsPerPortion <= 0
  ) {
    return false;
  }
  if (barG <= 0) return false;
  return barG / barCarbsPerPortion >= 0.95;
}

/**
 * Beskriver hur stor del av en bar per timme-intaget motsvarar när bars delas
 * upp över flera timmar (t.ex. "½ bar", "¾ bar", "1 bar", "1½ bar"). Returnerar
 * null om vi saknar data om bar-storleken.
 */
function barFractionLabelSv(
  barG: number,
  barCarbsPerPortion: number | null | undefined
): string | null {
  if (
    barCarbsPerPortion == null ||
    !Number.isFinite(barCarbsPerPortion) ||
    barCarbsPerPortion <= 0
  ) {
    return null;
  }
  if (barG <= 0) return null;
  const frac = barG / barCarbsPerPortion;
  if (frac < 0.2) return `ca ${Math.round(frac * 100)} % av en bar`;
  if (frac < 0.3) return "ca ¼ bar";
  if (frac < 0.42) return "ca ⅓ bar";
  if (frac < 0.6) return "ca ½ bar";
  if (frac < 0.78) return "ca ⅔ bar";
  if (frac < 0.95) return "ca ¾ bar";
  if (frac < 1.1) return "ca 1 bar";
  const whole = Math.floor(frac);
  const rest = frac - whole;
  if (rest < 0.2) return `ca ${whole} bar`;
  if (rest < 0.6) return `ca ${whole}½ bar`;
  return `ca ${whole}–${whole + 1} bar`;
}

/**
 * Bygger intagsplan uppdelad per gren för triathlon när användaren har angett
 * individuella tider. Använder produkter från paketförslagets segment-rader så
 * planen speglar det som faktiskt ligger i förslaget.
 */
function buildTriathlonSegmentedRaceItems(args: {
  raceItems: IntakeTimelineRaceItem[];
  split: TriathlonSplitTimes;
  carbsPerHour: number;
  raceLines: PackageLine[];
  firstGelFallback: CalcProduct | null;
}): void {
  const { raceItems, split, carbsPerHour, raceLines, firstGelFallback } = args;
  const swimH = totalHoursFromParts(split.swimH, split.swimM);
  const bikeH = totalHoursFromParts(split.bikeH, split.bikeM);
  const runH = totalHoursFromParts(split.runH, split.runM);

  const segments: Array<{
    segment: TriathlonSegment;
    hours: number;
    startH: number;
  }> = [
    { segment: "swim", hours: swimH, startH: 0 },
    { segment: "bike", hours: bikeH, startH: swimH },
    { segment: "run", hours: runH, startH: swimH + bikeH },
  ];

  for (const seg of segments) {
    if (seg.hours <= 0) continue;
    const segStartMin = seg.startH * 60;
    const segEndMin = (seg.startH + seg.hours) * 60;

    const segLines = raceLines.filter((l) => l.segment === seg.segment);

    const summary =
      seg.segment === "swim"
        ? "Sim: oftast ingen energi i vattnet — ladda direkt före start och ät så snart du är uppe på cykeln."
        : seg.segment === "bike"
          ? "Cykel: fördela intaget jämnt; sportdryck i flaskor + bar/gel i fickan."
          : "Löpning: gel i intervall och drick vid stationer — håll magen lätt.";

    raceItems.push({
      kind: "triathlon_segment_header",
      segment: seg.segment,
      label: TRIATHLON_SEGMENT_LABEL_SV[seg.segment],
      startTimeLabel:
        segStartMin <= 0 ? "start" : formatMinutesFromRaceStart(segStartMin),
      durationLabel: formatDurationShortSv(seg.hours),
      summary,
    });

    if (seg.segment === "swim") {
      continue;
    }

    if (seg.segment === "bike") {
      const matSeg = segLines.filter((l) => l.categoryKey === "mat");
      const dryckSeg = segLines.filter((l) => l.categoryKey === "dryck");
      const gelSeg = segLines.filter((l) => l.categoryKey === "gel");
      const hasGel = gelSeg.length > 0;
      const barImage = matSeg[0]?.product.image ?? null;
      const dryckImage = dryckSeg[0]?.product.image ?? null;
      const gelImageRow = hasGel ? gelSeg[0]?.product.image ?? null : null;
      const barCarbsPerPortion = matSeg[0]?.product.carbs ?? null;
      const shares = sportNutritionProfileForTriathlonSegment("bike");
      const nHours = Math.max(1, Math.ceil(seg.hours));
      for (let hi = 1; hi <= nHours; hi++) {
        const barG = Math.round(carbsPerHour * shares.mat);
        const gelG = hasGel ? Math.round(carbsPerHour * shares.gel) : 0;
        const dryckG = Math.max(0, carbsPerHour - barG - gelG);
        const barFrac = barFractionLabelSv(barG, barCarbsPerPortion);
        const barLabel = barFrac ? `${barFrac} (${barG} g kh)` : `${barG} g bar`;
        const hourSummary =
          hasGel && gelG > 0
            ? `Mål ca ${carbsPerHour} g kh: ${barLabel}, ${gelG} g gel och ${dryckG} g från sportdryck.`
            : `Mål ca ${carbsPerHour} g kh: ${barLabel} och ${dryckG} g från sportdryck.`;
        const wholeBarCarbsG = isWholeBarPerHour(barG, barCarbsPerPortion)
          ? barG
          : null;
        raceItems.push({
          kind: "cycling_hour",
          hourIndex: hi,
          timeLabel: `${hi} h`,
          summary: hourSummary,
          barImage,
          dryckImage,
          gelImage: gelImageRow,
          wholeBarCarbsG,
        });
      }
      continue;
    }

    // run
    const gelSeg = segLines.filter((l) => l.categoryKey === "gel");
    const firstGelForRun = gelSeg[0]?.product ?? firstGelFallback;
    let avgGelG = 30;
    let totalGelUnits = 0;
    let totalGelCarbs = 0;
    for (const l of gelSeg) {
      totalGelUnits += l.units;
      totalGelCarbs += l.carbsDelivered;
    }
    if (totalGelUnits > 0) {
      avgGelG = Math.max(1, Math.round(totalGelCarbs / totalGelUnits));
    }
    const intervalMin =
      carbsPerHour > 0 ? (avgGelG / carbsPerHour) * 60 : 45;
    if (intervalMin <= 0) continue;
    let t = segStartMin + intervalMin;
    let n = 0;
    while (t <= segEndMin + 0.001 && n < INTAKE_TIMELINE_MAX_GEL_TICKS) {
      const p = firstGelForRun;
      const tickSummary = p
        ? `Ca ${avgGelG} g kh (t.ex. ${p.name}) — följ tabellen för exakt smak och antal.`
        : `Ca ${avgGelG} g kh per intag — se tabellen.`;
      raceItems.push({
        kind: "gel_tick",
        minutesFromStart: t,
        timeLabel: formatMinutesFromRaceStart(t),
        lineSummary: tickSummary,
        image: p?.image ?? null,
        carbsG: avgGelG,
      });
      t += intervalMin;
      n++;
    }
  }
}

/**
 * Tidslinjemodell för intagsplan: före start från tabellrader; under loppet =
 * gel-intervall (löpning/skidåkning/triathlon utan split) eller timvis bar+dryck
 * (cykel). För triathlon med separata tider per gren bygger vi per-segment.
 */
export function buildIntakeTimeline(args: {
  hoursTotal: number;
  carbsPerHour: number;
  targetDuring: number;
  preloadCarbs: number;
  sport: EnergySport;
  triathlonSplitMode: boolean;
  /** Krävs för per-gren-intagsplan vid triathlon-split. */
  triathlonSplit?: TriathlonSplitTimes | null;
  caffeineMode: CaffeineFilterMode;
  includeBottles: boolean;
  bottleCount: number;
  resolvedLines: PackageLine[];
  bodyWeightKg?: number | null;
  preraceMgPerKg?: number;
  /** Samma urval som i tabellen för sumPreraceCaffeineMgBeforeIndex (t.ex. bortvalda rader). */
  includePreraceLineInCaffeineSum?: (resolvedIndex: number) => boolean;
}): IntakeTimelineData | null {
  const {
    hoursTotal,
    carbsPerHour,
    targetDuring,
    preloadCarbs,
    sport,
    triathlonSplitMode,
    triathlonSplit = null,
    caffeineMode,
    includeBottles,
    bottleCount,
    resolvedLines,
    bodyWeightKg = null,
    preraceMgPerKg = DEFAULT_PRERACE_MG_PER_KG,
    includePreraceLineInCaffeineSum,
  } = args;
  if (hoursTotal <= 0 || targetDuring <= 0) return null;

  const durationLabel = formatDurationSv(hoursTotal);

  let caffeineMgTotal = 0;
  const prerace: IntakeTimelinePreraceRow[] = [];
  for (let resolvedIndex = 0; resolvedIndex < resolvedLines.length; resolvedIndex++) {
    const line = resolvedLines[resolvedIndex]!;
    if (line.preraceIntakeHint == null) continue;
    const p = line.product;
    const hint = line.preraceIntakeHint ?? "";
    const minutesBeforeStart = parsePreraceHintMinutesBeforeStart(hint);
    const prior = sumPreraceCaffeineMgBeforeIndex(
      resolvedLines,
      resolvedIndex,
      includePreraceLineInCaffeineSum
    );
    const tabletRec = getPreraceCaffeineTabletRecommendation(
      p,
      bodyWeightKg,
      prior,
      preraceMgPerKg
    );
    let preraceQuantityLabel: string | null = null;
    let unitsDisplay = Math.max(1, line.units);
    let cafMg: number | null = null;
    if (tabletRec != null) {
      preraceQuantityLabel =
        tabletRec.rawLo === tabletRec.rawHi
          ? `${tabletRec.rawHi} tabletter`
          : `${tabletRec.rawLo}–${tabletRec.rawHi} tabletter`;
      unitsDisplay = tabletRec.rawHi;
      cafMg = Math.round(tabletRec.rawHi * tabletRec.mgPer);
    } else if (
      productCaffeineKind(p) === "contains" &&
      p.caffeine_mg_per_serving != null &&
      Number.isFinite(p.caffeine_mg_per_serving) &&
      p.caffeine_mg_per_serving > 0
    ) {
      cafMg = Math.round(p.caffeine_mg_per_serving * Math.max(1, line.units));
    }
    if (cafMg != null && cafMg > 0) {
      caffeineMgTotal += cafMg;
    }
    prerace.push({
      minutesBeforeStart,
      timeLabel: formatTimeBeforeStartLabel(minutesBeforeStart),
      productName: p.name,
      units: unitsDisplay,
      preraceQuantityLabel,
      carbsG: Math.round(line.carbsDelivered),
      caffeineMg: cafMg,
      isNitrate: p.id === PRERACE_PRODUCT_NITRATE_ID,
      image: p.image,
    });
  }
  prerace.sort((a, b) => b.minutesBeforeStart - a.minutesBeforeStart);

  const raceLines = resolvedLines.filter(
    (l) => l.preraceIntakeHint == null && !l.accessory
  );
  const gelRaceLines = raceLines.filter((l) => l.categoryKey === "gel");
  const firstGel = gelRaceLines[0]?.product ?? null;

  const raceItems: IntakeTimelineRaceItem[] = [];

  let cyclingPlan: IntakeTimelineData["cyclingPlan"] = null;

  if (sport === "triathlon" && triathlonSplitMode && triathlonSplit != null) {
    buildTriathlonSegmentedRaceItems({
      raceItems,
      split: triathlonSplit,
      carbsPerHour,
      raceLines,
      firstGelFallback: firstGel,
    });
  } else if (sport === "cycling") {
    const sh = sportNutritionProfile("cycling");
    const matRaceLines = raceLines.filter((l) => l.categoryKey === "mat");
    const dryckRaceLines = raceLines.filter((l) => l.categoryKey === "dryck");
    const gelCyclingLines = raceLines.filter((l) => l.categoryKey === "gel");
    const hasGel = gelCyclingLines.length > 0;
    const barImage = matRaceLines[0]?.product.image ?? null;
    const dryckImage = dryckRaceLines[0]?.product.image ?? null;
    const gelImageRow = hasGel ? gelCyclingLines[0]?.product.image ?? null : null;
    const barCarbsPerPortion = matRaceLines[0]?.product.carbs ?? null;
    const nHours = Math.max(1, Math.ceil(hoursTotal));
    let dryckTotalG = 0;
    let dryckPerHourG = 0;
    for (let hi = 1; hi <= nHours; hi++) {
      const barG = Math.round(carbsPerHour * sh.mat);
      const gelG = hasGel ? Math.round(carbsPerHour * sh.gel) : 0;
      const dryckG = Math.max(0, carbsPerHour - barG - gelG);
      dryckPerHourG = dryckG;
      dryckTotalG += dryckG;
      const barFrac = barFractionLabelSv(barG, barCarbsPerPortion);
      const barLabel = barFrac ? `${barFrac} (${barG} g kh)` : `${barG} g bar`;
      const summary =
        hasGel && gelG > 0
          ? `Mål ca ${carbsPerHour} g kh: ${barLabel}, ${gelG} g gel och ${dryckG} g från sportdryck.`
          : `Mål ca ${carbsPerHour} g kh: ${barLabel} och ${dryckG} g från sportdryck.`;
      const wholeBarCarbsG = isWholeBarPerHour(barG, barCarbsPerPortion)
        ? barG
        : null;
      raceItems.push({
        kind: "cycling_hour",
        hourIndex: hi,
        timeLabel: `Timme ${hi}`,
        summary,
        barImage,
        dryckImage,
        gelImage: gelImageRow,
        wholeBarCarbsG,
      });
    }
    cyclingPlan = {
      totalDryckG: dryckTotalG,
      perHourDryckG: dryckPerHourG,
    };
  } else {
    let avgGelG = 30;
    let totalGelUnits = 0;
    let totalGelCarbs = 0;
    for (const l of gelRaceLines) {
      totalGelUnits += l.units;
      totalGelCarbs += l.carbsDelivered;
    }
    if (totalGelUnits > 0) {
      avgGelG = Math.max(1, Math.round(totalGelCarbs / totalGelUnits));
    }
    const intervalMin =
      carbsPerHour > 0 ? (avgGelG / carbsPerHour) * 60 : 45;
    const raceMin = hoursTotal * 60;
    let t = intervalMin;
    let n = 0;
    while (t <= raceMin + 0.001 && n < INTAKE_TIMELINE_MAX_GEL_TICKS) {
      const p = firstGel;
      const summary = p
        ? `Ca ${avgGelG} g kh (t.ex. ${p.name}) — följ tabellen för exakt smak och antal.`
        : `Ca ${avgGelG} g kh per intag — se tabellen.`;
      raceItems.push({
        kind: "gel_tick",
        minutesFromStart: t,
        timeLabel: formatMinutesFromRaceStart(t),
        lineSummary: summary,
        image: p?.image ?? null,
        carbsG: avgGelG,
      });
      t += intervalMin;
      n++;
    }
    if (raceItems.length === 0) {
      raceItems.push({
        kind: "race_note",
        summary:
          "Inga geler i förslaget just nu — följ tabellen för bar, dryck och övrigt under loppet.",
      });
    }
  }

  const bottleLine =
    includeBottles && bottleCount > 0
      ? `Flaskor i förslaget: ca ${bottleCount} st (vätska till sportdryck). `
    : "";
  const intro = `${bottleLine}Siffrorna är riktlinjer — drick vatten vid stationer och läs säkerhetsinformation på produkterna.`;

  return {
    stats: {
      durationLabel,
      carbsPerHour,
      totalCarbs: targetDuring,
      caffeineMgTotal,
    },
    prerace,
    raceItems,
    cyclingPlan,
    notes: {
      intro,
      preload: ``,
      afterGoal:
        "Efter mål: vätska, kolhydrater och lite protein inom 1–2 timmar.",
    },
  };
}

function formatNum(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, "");
}

function formatDurationSv(hoursTotal: number): string {
  if (hoursTotal <= 0) return "—";
  const totalSec = Math.round(hoursTotal * 3600);
  if (totalSec <= 0) return "—";
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const parts: string[] = [];
  if (h > 0) parts.push(`${h} h`);
  if (m > 0) parts.push(`${m} min`);
  if (s > 0) parts.push(`${s} s`);
  if (parts.length === 0) return "—";
  return parts.join(" ");
}
